/**
 * GA4 for the application, loaded only when VITE_GA_MEASUREMENT_ID is set.
 *
 * Why the app needs its own tag at all: server-side Measurement Protocol
 * events (sign_up, retrieval_denied) can only be joined to a session that
 * GA4 knows about, and GA4 only knows about sessions created by gtag on a
 * tracked page. Before this existed, the marketing site created the
 * session, the user moved to app.gateco.ai where nothing ran, the session
 * timed out after 30 minutes of inactivity, and every server-side event
 * landed on a dead session id and reported "(not set)".
 *
 * Two properties of this setup are load-bearing, do not change them
 * casually:
 *  - Same measurement id as the marketing site. A second property would
 *    mean a second identity space and no join at all.
 *  - Default cookie domain ("auto"), which resolves to .gateco.ai and is
 *    exactly what the marketing site uses. That shared cookie is what
 *    carries client id across the subdomain hop.
 *  - The linker domain list below, which must match the marketing site's.
 *
 * Corrected 2026-09-15: the shared cookie alone was not enough. It carried
 * client_id, so users were not double-counted, but GA4 still saw the arrival
 * from gateco.ai as a referral, opened a fresh session, and attributed it to
 * gateco.ai rather than to whatever brought the user in. Server-side sign_up
 * then joined a session whose source was already lost, and reported (not set).
 * The linker config treats the two as one property. The matching GA4 Admin
 * setting (Data Streams > Configure tag settings > Configure your domains)
 * must list the same domains; the tag alone does not set referral exclusion.
 */

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let initialized = false;

/**
 * Load gtag and configure it. Safe to call more than once; only the first
 * call does anything. No-ops entirely when the measurement id is unset, so
 * local development and preview builds never write to production analytics.
 */
export function initAnalytics(): void {
  if (initialized || !MEASUREMENT_ID || typeof window === 'undefined') return;
  initialized = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };
  window.gtag('js', new Date());
  // send_page_view is off because this is a single-page app: gtag would
  // count only the first render and miss every client-side route change.
  // trackPageView below is called for the initial view and each navigation.
  window.gtag('config', MEASUREMENT_ID, {
    send_page_view: false,
    // Same domain list as the marketing site. The shared .gateco.ai cookie
    // already carried client_id across the hop, but the app still looked like
    // a referral from gateco.ai, so GA4 opened a new session and dropped the
    // acquisition source. accept_incoming consumes the _gl the site appends.
    linker: {
      domains: ['gateco.ai', 'www.gateco.ai', 'app.gateco.ai'],
      accept_incoming: true,
    },
  });
}

/**
 * Record a page view for a client-side route.
 *
 * Paths are sent as-is. Route paths here are app-internal and carry no
 * query strings or personal data; if that ever changes, strip the query
 * before it reaches GA4 rather than after.
 */
export function trackPageView(path: string): void {
  if (!MEASUREMENT_ID || typeof window === 'undefined' || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: document.title,
  });
}

/** Whether analytics is configured. Exposed for tests and diagnostics. */
export function isAnalyticsEnabled(): boolean {
  return Boolean(MEASUREMENT_ID);
}
